/**
 * Champ de recherche d'un joueur (autocomplete texte → pseudo), tous joueurs
 * confondus (pas seulement les membres de l'équipe).
 *
 * Utilisé pour inviter un joueur au mercato : chaque résultat affiche son
 * avatar et son statut (déjà dans une équipe, ou libre). Un joueur déjà en
 * équipe reste sélectionnable (on peut vouloir le débaucher) mais son
 * équipe actuelle est indiquée pour éviter les erreurs.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { PlayerAvatar } from '../PlayerAvatar';
import { BACKEND_URL } from '../../config';

export interface PlayerPickerItem {
  pseudo: string;
  pseudoDisplay: string;
  photoUrl?: string | null;
  team?: { id: number; nom: string } | null;
}

interface PlayerPickerProps {
  value: PlayerPickerItem | null;
  onChange: (player: PlayerPickerItem | null) => void;
  placeholder?: string;
  excludePseudos?: string[]; // ex. membres déjà dans l'équipe
  autoFocus?: boolean;
}

export default function PlayerPicker({ value, onChange, placeholder = 'Rechercher un joueur…', excludePseudos, autoFocus }: PlayerPickerProps) {
  const [query, setQuery] = useState(value?.pseudoDisplay || '');
  const [results, setResults] = useState<PlayerPickerItem[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setQuery(value?.pseudoDisplay || ''); }, [value]);

  const excludeSet = new Set((excludePseudos || []).map((p) => p.toLowerCase()));

  const runSearch = useCallback((q: string) => {
    if (q.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    fetch(`${BACKEND_URL}/api/players/search?q=${encodeURIComponent(q.trim())}&limit=10&compet=true`)
      .then((res) => res.json())
      .then((json) => {
        const players: PlayerPickerItem[] = (json.players || []).filter(
          (p: PlayerPickerItem) => !excludeSet.has(p.pseudo.toLowerCase())
        );
        setResults(players);
      })
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludePseudos?.join(',')]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query), 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, runSearch]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const select = (p: PlayerPickerItem) => {
    onChange(p);
    setQuery(p.pseudoDisplay);
    setOpen(false);
  };

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {value && <PlayerAvatar pseudo={value.pseudo} size={32} photoUrl={value.photoUrl} />}
        <input
          type="text"
          autoFocus={autoFocus}
          placeholder={placeholder}
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); if (value) onChange(null); }}
          style={{
            flex: 1, padding: '9px 12px', borderRadius: 8,
            border: '1px solid var(--border-color)', background: 'var(--input-bg, rgba(0,0,0,0.2))', color: 'var(--text-primary)',
          }}
        />
      </div>

      {value?.team && (
        <div style={{ marginTop: 6, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
          ⚠️ Déjà dans une équipe : <strong>{value.team.nom}</strong>
        </div>
      )}

      {open && (query.trim().length >= 2) && (
        <div className="tm-select-panel" style={{ position: 'absolute' }}>
          {loading && (
            <div style={{ padding: '10px 12px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Recherche…</div>
          )}
          {!loading && results.length === 0 && (
            <div style={{ padding: '10px 12px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              Aucun joueur trouvé
            </div>
          )}
          {!loading && results.map((p) => (
            <button
              key={p.pseudo}
              type="button"
              className="tm-select-option"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => select(p)}
            >
              <PlayerAvatar pseudo={p.pseudo} size={26} photoUrl={p.photoUrl} />
              <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.pseudoDisplay}</span>
                <span style={{ color: p.team ? 'var(--text-secondary)' : 'var(--text-muted)', fontSize: '0.7rem' }}>
                  {p.team ? `Équipe : ${p.team.nom}` : 'Sans équipe'}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
